import { useCallback, useRef } from 'react';
import { setLoginToken } from './donorLocalStorage';
import { usePushEvents } from '../../hooks/PushEvents';
import {
    errorOnSignIn,
    signInSuccessful,
} from '../../hooks/PushEvents/clevertapEvents';
import { logInfo } from '../../utils/logError';
import { useDonorContext } from './DonorProvider';
import { removeQueryString } from '../../hooks/useQueryString';
import {
    ApiHandler,
    ApiResponse,
    ErrorResponse,
    FacebookLoginRequest,
    LoginResponse,
    useDonorProfile,
} from '../../api';

type LoginRequest = () => Promise<ApiResponse<LoginResponse>>;

export const useDonorLogin = () => {
    const { setDonorContext, loadDonor } = useDonorContext();
    const { donorProfile, updateLocalDonorProfile } = useDonorProfile();
    const donorEmail = useRef<string | undefined>(undefined);
    const { pushEvent } = usePushEvents();

    const performLogin = useCallback(
        async (
            emailAddress: string | undefined,
            request: LoginRequest,
            successCallback?: () => void,
            errorCallback?: (error: ErrorResponse) => void,
        ) => {
            donorEmail.current = emailAddress;
            setDonorContext((s) => ({
                ...s,
                loginResponse: {
                    data: null,
                    error: null,
                    loading: true,
                },
            }));

            const apiRequest = await request();
            if (!apiRequest.success) {
                setDonorContext((s) => ({
                    ...s,
                    loginResponse: {
                        data: null,
                        error: apiRequest.error,
                        loading: false,
                    },
                }));
                pushEvent(errorOnSignIn, { donorEmail: donorEmail.current });
                if (errorCallback) errorCallback(apiRequest.error);
                return;
            }

            const { payload } = apiRequest.data.response;
            setDonorContext((s) => ({
                ...s,
                loginResponse: {
                    data: payload,
                    error: null,
                    loading: false,
                },
            }));
            setLoginToken(payload);
            loadDonor(true);
            removeQueryString('login');
            pushEvent(signInSuccessful, {
                donorId: payload.donorId,
                donorEmail: donorEmail.current,
            });
            logInfo(
                `${donorEmail.current || 'Unknown User'} logged in successfully.`,
                {
                    id: donorEmail.current,
                    donorEmail: donorEmail.current,
                    ogaVersions: '4.0',
                },
            );
            successCallback?.();
        },
        [loadDonor, pushEvent, setDonorContext],
    );

    const setProfilePicture = useCallback(
        (pic?: string) => {
            if (!pic) return;
            if (!donorProfile) return;
            updateLocalDonorProfile({
                ...donorProfile,
                donor: {
                    ...donorProfile.donor,
                    pictureUrl: pic,
                },
            });
        },
        [donorProfile, updateLocalDonorProfile],
    );

    const emailLogin = useCallback(
        async (
            username: string,
            password: string,
            successCallback?: () => void,
            errorCallback?: (error: ErrorResponse) => void,
        ) => {
            await performLogin(
                username,
                () =>
                    ApiHandler.instance.auth.loginByEmail({
                        emailAddress: username,
                        password,
                    }),
                successCallback,
                errorCallback,
            );
        },
        [performLogin],
    );

    const facebookLogin = useCallback(
        async (body: FacebookLoginRequest) => {
            await performLogin(body.emailAddress, () =>
                ApiHandler.instance.auth.loginByFacebook(body),
            );
        },
        [performLogin],
    );

    return {
        setProfilePicture,
        emailLogin,
        facebookLogin,
    };
};
